'use client';

import { useEffect } from 'react';
import { useParams, usePathname } from 'next/navigation';
import { LoadError, useOffline } from '@/components/load-error';
import { knownTitleFor } from '@/components/loading-chrome';
import { Modal } from '@/components/modal';
import { PathTrail } from '@/components/path-trail';
import { shellBackFor } from '@/components/shell-back';
import { useShellNames } from '@/components/shell-names';
import { useTapTarget } from '@/components/tap-mount-store';
import { TopbarSlot } from '@/components/topbar-slot';
import { PageTitle } from '@/components/ui';
import { useBack } from '@/components/use-back';
import { BackOnlyDock } from './items/[itemId]/section-dock';
import activityStyles from './activity/activity.module.css';
import itemStyles from './items/[itemId]/drilldown.module.css';
import sectionStyles from './items/[itemId]/section-screen.module.css';
import lensStyles from './views/lens.module.css';
import tagStyles from './tags/tags.module.css';
import { useT } from '@/lib/locale-context';
import styles from './error-screens.module.css';

/**
 * The routes' error boundaries (docs/LOADING.md §2 tool 10, §4.5): a
 * navigation whose render throws shows the error state in the SKELETON'S
 * LAYOUT, the same frame the route's `loading.tsx` draws, so the screen the
 * person asked for is still the one on screen. The shapes go; one line says
 * what failed; Retry (the boundary's `retry`, which re-fetches the segment and
 * renders it again) and Back (the rules of `useBack`).
 * One `error.tsx` beside each `loading.tsx`, and one at the root.
 *
 * An item surface also does what `not-found.tsx` does: it fills the top bar's
 * slot with the trail's Back alone and claims the back-only dock, because the
 * shell's loading chrome stands on an item route until the page fills the one
 * and claims the other (docs/LOADING.md §4.2); without them a dead route kept
 * the loading dock for good. A page that is not an item keeps the nav lanes,
 * which are what its own page would show, and fills the top bar's slot with
 * the title the shell knows for the route, or with nothing: left to the
 * shell, the slot kept the loading chrome's title-sized shape over the error
 * where only the page's data knows the title.
 */

/** What every error boundary receives from Next (16.3: `retry` re-fetches and
 *  re-renders; `reset` only re-renders, which throws again when the SERVER
 *  render was what failed). */
export interface RouteErrorProps {
  error: Error & { digest?: string };
  retry: () => void;
}

/** The thrown error in the console, as Next's default does: the digest is what
 *  matches a server-side log line. */
function useReport(error: RouteErrorProps['error']): void {
  useEffect(() => {
    console.error(error);
  }, [error]);
}

/** The item's full page (`items/[itemId]/error.tsx`): the page's `main`. */
export function ItemPageError({ error, retry }: RouteErrorProps) {
  useReport(error);
  const offline = useOffline();
  const back = useBack('/work');
  return (
    <>
      <LoneTrail />
      <BackOnlyDock fallbackHref="/work" />
      <main className={itemStyles.main}>
        <LoadError
          what="item"
          offline={offline}
          onRetry={retry}
          onBack={back}
          testId="route-error"
        />
      </main>
    </>
  );
}

/** The item's peek (`@modal/(.)items/[itemId]/error.tsx`): the peek's frame,
 *  so the list behind stays and Esc still closes it. */
export function ItemPeekError({ error, retry }: RouteErrorProps) {
  const t = useT();
  useReport(error);
  const offline = useOffline();
  const back = useBack('/work');
  return (
    <Modal
      label={t('shell.loadError.title')}
      fallbackHref="/work"
      toolbar={<PathTrail items={[]} />}
    >
      <BackOnlyDock fallbackHref="/work" />
      <LoadError what="item" offline={offline} onRetry={retry} onBack={back} testId="route-error" />
    </Modal>
  );
}

/** The href of the item a section screen belongs to: where its Back walks when
 *  popping would leave the app, as `SectionDock`'s does. */
function useItemHref(): string {
  const params = useParams<{ itemId?: string }>();
  // A fallback for a Back with no in-app history to pop: a fresh arrival at
  // the item, so no trail to carry.
  // eslint-disable-next-line no-restricted-syntax -- the fallback of a back-only dock, not a drilldown link; it starts a new trail by design.
  return params.itemId ? `/items/${params.itemId}` : '/work';
}

/** A section screen on the full page (`items/[itemId]/<section>/error.tsx`). */
export function SectionPageError({ error, retry }: RouteErrorProps) {
  useReport(error);
  const offline = useOffline();
  const itemHref = useItemHref();
  const back = useBack(itemHref);
  return (
    <>
      <LoneTrail />
      <BackOnlyDock fallbackHref={itemHref} />
      <main className={sectionStyles.main}>
        <LoadError
          what="item"
          offline={offline}
          onRetry={retry}
          onBack={back}
          testId="route-error"
        />
      </main>
    </>
  );
}

/** A section screen pushed inside the peek
 *  (`@modal/(.)items/[itemId]/<section>/error.tsx`). */
export function SectionPeekError({ error, retry }: RouteErrorProps) {
  const t = useT();
  useReport(error);
  const offline = useOffline();
  const itemHref = useItemHref();
  const back = useBack(itemHref);
  return (
    <Modal
      label={t('shell.loadError.title')}
      fallbackHref={itemHref}
      toolbar={<PathTrail items={[]} />}
    >
      <BackOnlyDock fallbackHref={itemHref} />
      <LoadError what="item" offline={offline} onRetry={retry} onBack={back} testId="route-error" />
    </Modal>
  );
}

/** The frames a page that is not an item loads into, each its skeleton's:
 *  the floor and the lens share the lens page's `main`. */
export type PageFrame = 'floor' | 'lens' | 'activity' | 'tag';

/** A page that is not an item: Work, a lens, Activity and its entry, the tag
 *  explorer's column, and the root for every other route (a board included). */
export function PageError({ error, retry, frame }: RouteErrorProps & { frame: PageFrame }) {
  useReport(error);
  const offline = useOffline();
  // The same walk as the dock's Back on compact, which stands on this screen
  // too: two Backs on one screen must not lead to two places.
  const back = useBack(shellBackFor(usePathname()) ?? '/work');
  const body = (
    <LoadError what="page" offline={offline} onRetry={retry} onBack={back} testId="route-error" />
  );
  switch (frame) {
    case 'activity':
      return (
        <>
          <PageErrorTitle />
          <div className={activityStyles.page}>
            <div className={styles.pad}>{body}</div>
          </div>
        </>
      );
    case 'tag':
      // Inside the explorer's layout, which keeps the rail and fills the top
      // bar's slot with its own title: the column only.
      return <div className={tagStyles.columnBody}>{body}</div>;
    default:
      return (
        <>
          <PageErrorTitle />
          <main className={lensStyles.main} data-frame={frame}>
            <div className={styles.head}>{body}</div>
          </main>
        </>
      );
  }
}

/** The top bar on a page that did not load: the title the shell's loading
 *  chrome would draw for the route (`knownTitleFor`: a static name, a view's
 *  or a group's name the shell holds, the tapped row's title), or nothing.
 *  Filling the slot takes the chrome's seat out of the bar, its shape with it. */
function PageErrorTitle() {
  const t = useT();
  const pathname = usePathname();
  const tapTarget = useTapTarget();
  const shell = useShellNames();
  const title = knownTitleFor(pathname, tapTarget, shell);
  return <TopbarSlot>{title && <PageTitle>{t.msg(title)}</PageTitle>}</TopbarSlot>;
}

/** The top bar on an item surface that did not load: the trail's Back alone,
 *  no crumbs, as `not-found.tsx` draws it. */
function LoneTrail() {
  return (
    <TopbarSlot>
      <div className={itemStyles.headerBar}>
        <PathTrail items={[]} />
      </div>
    </TopbarSlot>
  );
}
